import {
    type IRelayEndpointResponseDto,
    RelayAgentHealthStatus,
    RelayHealthStatus,
} from '@experiences/interfaces';
import {
    useNavigateWithParams,
    useQueryState,
    useRouteResolver,
} from '@experiences/util';
import type { IGridFilters } from '@uipath/portal-shell-react';
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import useSWR from 'swr';

import { routePaths } from '../../common/constants/routePaths';
import {
    bulkHealthCheck,
    getRelayGroupAgents,
    getRelayGroupDetails,
    getRelayGroupEndpoints,
} from '../../services/relay/RelayService';
import { accountGlobalId } from '../../store/selectors';

function mapHealthValue(value: unknown): RelayHealthStatus | undefined {
    // RelayHealthStatus string enum
    if (value === RelayHealthStatus.Live) {
        return RelayHealthStatus.Live;
    }
    if (value === RelayHealthStatus.Warning) {
        return RelayHealthStatus.Warning;
    }
    if (value === RelayHealthStatus.Offline) {
        return RelayHealthStatus.Offline;
    }

    // Numeric: 0 = Offline, 1 = Warning, >= 2 = Live
    if (typeof value === 'number') {
        if (value >= 2) {
            return RelayHealthStatus.Live;
        }
        if (value === 1) {
            return RelayHealthStatus.Warning;
        }
        return RelayHealthStatus.Offline;
    }
    // Boolean fallback
    if (value === true) {
        return RelayHealthStatus.Live;
    }
    if (value === false) {
        return RelayHealthStatus.Offline;
    }
    return undefined;
}

const useRelayGroupDetailsViewModel = () => {
    const { tenantId = '' } = useParams<{ tenantId: string }>();
    const initialTenantIdRef = useRef(tenantId);
    const accountId = useSelector(accountGlobalId);
    const { relayGroupId = '' } = useParams<{ relayGroupId: string }>();
    const navigate = useNavigateWithParams();
    const getRoute = useRouteResolver();

    const [ endpointPagination, setEndpointPagination ] = useQueryState<{
        top: number;
        skip: number;
        searchTerm?: string;
    }>('pagination', {
        top: 10,
        skip: 0,
        searchTerm: '',
    }, undefined, true);

    const isTenantStale = tenantId !== initialTenantIdRef.current;
    const relayGroupDetailsCacheKey = !isTenantStale && accountId && tenantId && relayGroupId
        ? [ 'relay-group-details', accountId, tenantId, relayGroupId ]
        : null;

    const {
        data: relayGroupDetails, isValidating: loading, mutate: relayGroupMutate,
    } = useSWR(
        relayGroupDetailsCacheKey,
        () => getRelayGroupDetails(accountId, tenantId, relayGroupId),
    );

    useEffect(() => {
        if (tenantId !== initialTenantIdRef.current) {
            navigate(getRoute(routePaths.adminTenantRelay.replace(':tenantId', tenantId)));
        }
    }, [ tenantId, navigate, getRoute ]);

    const relayEndpointsCacheKey = !isTenantStale && accountId && tenantId && relayGroupId
        ? [ 'relay-group-endpoints', accountId, tenantId, relayGroupId, endpointPagination.top, endpointPagination.skip, endpointPagination.searchTerm ]
        : null;

    const {
        data: relayEndpoints, isValidating: isEndpointLoading, mutate: mutateRelayEndpoints,
    } = useSWR(
        relayEndpointsCacheKey,
        () => getRelayGroupEndpoints(accountId, tenantId, relayGroupId, endpointPagination.top, endpointPagination.skip, endpointPagination.searchTerm),
    );

    const onEndpointsGridChange = useCallback(({
        pageIndex, pageSize, searchTerm,
    }: IGridFilters<IRelayEndpointResponseDto>) => {
        const pageSizeOrDefault = pageSize ?? 10;
        setEndpointPagination({
            top: pageSizeOrDefault,
            skip: (pageIndex ?? 0) * pageSizeOrDefault,
            searchTerm,
        });
    }, [ setEndpointPagination ]);

    const relayAgentsCacheKey = !isTenantStale && accountId && tenantId && relayGroupId
        ? [ 'relay-agents', accountId, tenantId, relayGroupId ]
        : null;

    const {
        data: relayAgents, isValidating: isRelayAgentsLoading, mutate: mutateRelayAgents,
    } = useSWR(
        relayAgentsCacheKey,
        () => getRelayGroupAgents(accountId, tenantId, relayGroupId),
    );

    const endpointIds = useMemo(() =>
        (relayEndpoints?.results ?? []).map(ep => ep.id),
    [ relayEndpoints ]);

    const agentIds = useMemo(() =>
        (relayAgents?.results ?? []).map(agent => agent.id),
    [ relayAgents ]);

    const hasIds = endpointIds.length > 0 || agentIds.length > 0;
    const healthCacheKey = !isTenantStale && accountId && tenantId && hasIds
        ? [ 'relay-bulk-health', accountId, tenantId, ...endpointIds, ...agentIds ]
        : null;

    const {
        data: healthData, mutate: mutateHealth,
    } = useSWR(
        healthCacheKey,
        () => bulkHealthCheck(accountId, tenantId, relayGroupId, {
            endpointUuids: endpointIds.length > 0 ? endpointIds : undefined,
            agentUuids: agentIds.length > 0 ? agentIds : undefined,
        }),
        {
            refreshInterval: 30000,
            // If the bulk health check indicates there's an agent mismatch, trigger a refetch of agents
            onSuccess: (data) => {
                if (data?.hasAgentMismatch) {
                    mutateRelayAgents();
                }
            },
        },
    );

    const healthStatusMap = useMemo(() => {
        const map: Record<string, RelayHealthStatus> = {};
        // Seed from endpoint connectivityStatus
        for (const ep of (relayEndpoints?.results ?? [])) {
            const mapped = mapHealthValue(ep.connectivityStatus);
            if (mapped) {
                map[ep.id] = mapped;
            }
        }
        // Override with health API data when available
        if (healthData?.endpoints) {
            for (const [ id, value ] of Object.entries(healthData.endpoints)) {
                const mapped = mapHealthValue(value);
                if (mapped) {
                    map[id] = mapped;
                }
            }
        }
        return map;
    }, [ relayEndpoints, healthData ]);

    const agentHealthStatusMap = useMemo(() => {
        const map: Record<string, RelayAgentHealthStatus> = {};
        // Seed from agent list response
        for (const agent of (relayAgents?.results ?? [])) {
            map[agent.id] = agent.healthStatus;
        }
        // Override with bulk health API data when available
        if (healthData?.agents) {
            for (const [ id, value ] of Object.entries(healthData.agents)) {
                if (value === RelayAgentHealthStatus.Active || value === RelayAgentHealthStatus.Inactive) {
                    map[id] = value;
                }
            }
        }
        return map;
    }, [ relayAgents, healthData ]);

    const refreshEndpoints = useCallback(async () => {
        // Clear stale health immediately so it doesn't override fresh endpoint data
        mutateHealth(undefined, { revalidate: false });
        // Fetch fresh endpoints
        await mutateRelayEndpoints();
    }, [ mutateRelayEndpoints, mutateHealth ]);

    return {
        relayGroupDetails,
        loading,
        relayGroupMutate,
        relayEndpoints,
        isEndpointLoading,
        mutateRelayEndpoints,
        refreshEndpoints,
        onEndpointsGridChange,
        endpointPagination,
        relayAgents,
        isRelayAgentsLoading,
        mutateRelayAgents,
        refreshAgents: mutateRelayAgents,
        healthStatusMap,
        agentHealthStatusMap,
    };
};

export default useRelayGroupDetailsViewModel;
